import { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../../App";
import PopUp from "../PopUp";
import api from "../api/api";
import functions from "../../functions/functions";
import PlusMinusLarge from "../ui/PlusMinusLarge";
import SplitPartialPopup from "./SplitPartialPopup";
import { useAppStore } from "../../store/useAppStore";

function SplitPopup({ options }) {
	const { setSplitPopup, setLoading, loading, setLoadingForced, setConfirm, loadOrderById, refreshOrdersState } =
		useContext(AppContext);
	const { setSplittedOrders, setSplittedOrdersCount } = useAppStore();
	const [tempCart, setTempCart] = useState({ ...options.order, romana: 2 });
	const [mode, setMode] = useState("products");

	const handleSave = () => {
		if (mode == "romana") {
			if (hasExistingPayments()) {
				setConfirm({
					title: "Attenzione!",
					message:
						"Su questo ordine è già presente un pagamento.\nProseguendo con la divisione alla romana il pagamento verrà eliminato.\nProseguire comunque?",
					onConfirm: () => {
						save();
					},
				});
			} else {
				save();
			}
		} else if (hasCoversUnassigned()) {
			setConfirm({
				title: "Attenzione!",
				message: "Non sono stati assegnati coperti al nuovo ordine\nProseguire comunque?",
				onConfirm: () => {
					save();
				},
			});
		} else {
			save();
		}
	};

	const hasExistingPayments = () => {
		return Array.isArray(tempCart.payments) && tempCart.payments.length > 0;
	};

	const hasCoversUnassigned = () => {
		let hasCoversInMain = false;
		let hasCoversInCopy = false;
		for (const product of tempCart.products) {
			if (product.is_cover == 1 && product.qty > 0) hasCoversInMain = true;
			if (product.is_cover == 1 && product.movedQty > 0) hasCoversInCopy = true;
		}
		return hasCoversInMain && !hasCoversInCopy;
	};

	const save = async () => {
		setLoadingForced(true);
		const res = await api.postProtected("/orders/split/" + getId() + "/", {
			mode: mode,
			romana: tempCart.romana,
			products: formatProducts(tempCart.products),
			partials: formatPartials(tempCart.partials || []),
		});
		if (res.success == 0) {
			setLoadingForced(false);
			alert(res.error);
		} else {
			await refreshOrdersState();
			if (options.mode == "cart") {
				setSplittedOrders(res.orders);
				setSplittedOrdersCount(res.orders.length);
				loadOrderById(res.orders[0]);
			}
			setSplitPopup(false);
			setLoadingForced(false);
		}
	};

	const getId = () => {
		let id = tempCart.id;
		if (options.mode == "cart") id = tempCart.order_id;
		return id;
	};

	const formatProducts = () => {
		if (options.mode == "list") return tempCart.products;
		else {
			let res = [];
			for (const p of tempCart.products) {
				res.push({ ...p, id: p.item_id });
			}
			return res;
		}
	};

	const formatPartials = (partials) => {
		return partials.map((p) => ({
			id: options.mode == "list" ? p.source_id : p.source_item_id,
			movedAmount: p.movedAmount,
		}));
	};

	const countCol2 = () => {
		let tot = 0;
		for (const product of tempCart.products) {
			if (product.movedQty) tot += parseInt(product.movedQty);
		}
		tot += (tempCart.partials || []).length;
		return tot;
	};

	return (
		<PopUp
			size=""
			customClass="split-popup"
			options={{
				onClose: () => setSplitPopup(false),
				title: "Dividi Ordine " + getId(),
			}}
		>
			<div className="inner-content">
				<div className="horizzontal-menu">
					<button
						onClick={() => setMode("products")}
						className={mode == "products" ? "active" : ""}
					>
						Per prodotti
					</button>
					<button
						onClick={() => setMode("romana")}
						className={mode == "romana" ? "active" : ""}
					>
						Alla romana
					</button>
				</div>
				{mode == "products" && (
					<ProductsMode tempCart={tempCart} setTempCart={setTempCart} />
				)}
				{mode == "romana" && <RomanaMode tempCart={tempCart} setTempCart={setTempCart} />}
			</div>
			<div className="popup-footer">
				{((mode == "products" && countCol2() > 0) || mode == "romana") && (
					<button className="button button-2" onClick={handleSave} disabled={loading}>
						Dividi
					</button>
				)}
			</div>
		</PopUp>
	);
}

const LONG_PRESS_MS = 500;

function ProductsMode({ tempCart, setTempCart }) {
	const [partialPopupIndex, setPartialPopupIndex] = useState(null);
	const longPressTimer = useRef(null);
	const longPressTriggered = useRef(false);

	const addToSelection = (i) => {
		console.log("addToSelection", i);
		if (countCol1() == 1) return;
		let nCart = { ...tempCart };
		nCart.products[i].qty--;
		if (nCart.products[i].movedQty) nCart.products[i].movedQty++;
		else nCart.products[i].movedQty = 1;
		setTempCart(nCart);
	};

	const removeFromSelection = (i) => {
		console.log("removeFromSelection", i);
		let nCart = { ...tempCart };
		nCart.products[i].qty++;
		if (nCart.products[i].movedQty) nCart.products[i].movedQty--;
		else delete nCart.products[i].movedQty;
		setTempCart(nCart);
	};

	const startLongPress = (i) => {
		const product = tempCart.products[i];
		if (parseInt(product.qty) < 1) return;
		if ((tempCart.partials || []).some((p) => p.source_index === i)) return;
		longPressTriggered.current = false;
		longPressTimer.current = setTimeout(() => {
			longPressTriggered.current = true;
			setPartialPopupIndex(i);
		}, LONG_PRESS_MS);
	};

	const cancelLongPress = () => {
		if (longPressTimer.current) {
			clearTimeout(longPressTimer.current);
			longPressTimer.current = null;
		}
	};

	const handleClick = (i) => {
		if (longPressTriggered.current) {
			longPressTriggered.current = false;
			return;
		}
		addToSelection(i);
	};

	const roundNumber = (number) => Math.round(number * 10) / 10;

	const addPartial = (i, amount) => {
		let nCart = { ...tempCart, partials: [...(tempCart.partials || [])], products: [...tempCart.products] };
		const product = nCart.products[i];
		nCart.products[i] = { ...product, price: roundNumber(product.price - amount) };
		nCart.partials.push({
			source_id: product.id,
			source_item_id: product.item_id,
			source_index: i,
			name: product.name,
			qty: product.qty,
			variants_categories: product.variants_categories,
			movedAmount: amount,
		});
		setTempCart(nCart);
	};

	const removePartial = (partialIndex) => {
		let nCart = { ...tempCart, partials: [...tempCart.partials], products: [...tempCart.products] };
		const partial = nCart.partials[partialIndex];
		nCart.partials.splice(partialIndex, 1);
		const i = partial.source_index;
		nCart.products[i] = {
			...nCart.products[i],
			price: roundNumber(nCart.products[i].price + partial.movedAmount),
		};
		setTempCart(nCart);
	};

	const getTotalCol1 = () => {
		let tot = 0;
		for (const product of tempCart.products) {
			tot += parseInt(product.qty) * parseFloat(product.price);
		}
		return functions.formatter.format(tot);
	};

	const getTotalCol2 = () => {
		let tot = 0;
		for (const product of tempCart.products) {
			if (product.movedQty) tot += parseInt(product.movedQty) * parseFloat(product.price);
		}
		for (const partial of tempCart.partials || []) {
			tot += parseInt(partial.qty) * parseFloat(partial.movedAmount);
		}
		return functions.formatter.format(tot);
	};

	const countCol1 = () => {
		let tot = 0;
		for (const product of tempCart.products) {
			tot += parseInt(product.qty);
		}
		return tot;
	};

	return (
		<>
			<div className="split-box">
				<div>
					{tempCart.products.map(
						(product, i) =>
							product.qty > 0 && (
								<button
									key={i}
									onClick={() => handleClick(i)}
									onMouseDown={() => startLongPress(i)}
									onMouseUp={cancelLongPress}
									onMouseLeave={cancelLongPress}
									onTouchStart={() => startLongPress(i)}
									onTouchEnd={cancelLongPress}
									onTouchMove={cancelLongPress}
								>
									<div className="product-container">
										<div className="product-name">
											{product.qty}x {product.name}
										</div>
										<div className="product-price">
											{functions.formatter.format(product.price)}
										</div>
									</div>
									<div className="product-variants">
										<Variants product={product} />
									</div>
								</button>
							)
					)}
				</div>
				<div>
					{tempCart.products.map(
						(product, i) =>
							product.movedQty > 0 && (
								<button key={i} onClick={() => removeFromSelection(i)}>
									<div className="product-container">
										<div className="product-name">
											{product.movedQty}x {product.name}
										</div>
										<div className="product-price">
											{functions.formatter.format(product.price)}
										</div>
									</div>
									<div className="product-variants">
										<Variants product={product} />
									</div>
								</button>
							)
					)}
					{(tempCart.partials || []).map((partial, i) => (
						<button key={"partial" + i} onClick={() => removePartial(i)}>
							<div className="product-container">
								<div className="product-name">
									{partial.qty}x {partial.name} (parziale)
								</div>
								<div className="product-price">
									{functions.formatter.format(partial.movedAmount)}
								</div>
							</div>
							<div className="product-variants">
								<Variants product={partial} />
							</div>
						</button>
					))}
				</div>
			</div>
			<div className="split-box totals">
				<div>{getTotalCol1()}</div>
				<div>{getTotalCol2()}</div>
			</div>
			{partialPopupIndex !== null && (
				<SplitPartialPopup
					product={tempCart.products[partialPopupIndex]}
					onClose={() => setPartialPopupIndex(null)}
					onConfirm={(amount) => {
						addPartial(partialPopupIndex, amount);
						setPartialPopupIndex(null);
					}}
				/>
			)}
		</>
	);
}

function Variants({ product }) {
	const [formattedVariants, setFormattedVariants] = useState([]);

	useEffect(() => {
		const res = [];
		product.variants_categories?.forEach((variants_category) => {
			variants_category.variants?.forEach((variant) => {
				if (variant.qty > 0) res.push({ name: variant.name, qty: variant.qty });
			});
			variants_category.variants2?.forEach((variant) => {
				if (variant.qty > 0) res.push({ name: variant.name, qty: variant.qty });
			});
			variants_category.products?.forEach((variant) => {
				if (variant.qty > 0) res.push({ name: variant.name, qty: variant.qty });
			});
			variants_category.products2?.forEach((variant) => {
				if (variant.qty > 0) res.push({ name: variant.name, qty: variant.qty });
			});
		});
		setFormattedVariants(res);
	}, [product]);

	return (
		<>
			{formattedVariants.map((variant, i) => (
				<span key={i}>
					{variant.qty}x {variant.name}
					{i < formattedVariants.length - 1 ? ", " : ""}
				</span>
			))}
		</>
	);
}

function RomanaMode({ tempCart, setTempCart }) {
	const { apiSettings } = useContext(AppContext);

	const getQuote = (i) => {
		//console.log("getQuote", i, tempCart.total, tempCart.romana, tempCart);
		let num = parseFloat(tempCart.total) / tempCart.romana;

		num = roundNumberBySettings(num);

		if (i == tempCart.romana - 1) {
			num = tempCart.total - getSumNotLast();
			num = roundNumber(num);
		}

		return num;
	};

	const getSum = () => {
		let sum = 0;
		for (let i = 0; i < tempCart.romana; i++) {
			sum += getQuote(i);
		}
		sum = roundNumber(sum);
		return sum;
	};

	const getSumNotLast = () => {
		let sum = 0;
		for (let i = 0; i < tempCart.romana - 1; i++) {
			sum = sum + getQuote(i);
		}
		sum = roundNumber(sum);
		return sum;
	};

	const roundNumber = (number, decimals = 2) => {
		var newnumber = new Number(number + "").toFixed(parseInt(decimals));
		return parseFloat(newnumber);
	};

	const roundNumberBySettings = (number) => {
		let decimals = 2;
		if (apiSettings.pos.romana_round == 2) decimals = 1;
		if (apiSettings.pos.romana_round == 3) decimals = 0;
		var newnumber = new Number(number + "").toFixed(parseInt(decimals));
		return parseFloat(newnumber);
	};

	return (
		<>
			<PlusMinusLarge
				value={tempCart.romana}
				onChange={(value) => setTempCart({ ...tempCart, romana: value })}
				min={2}
			/>
			<div className="quotes">
				{[...Array(tempCart.romana)].map((el, i) => (
					<div key={i}>
						<div className="quotes-header">Quota {i + 1}</div>
						<div className="quotes-content">
							{functions.formatter.format(getQuote(i))}
						</div>
					</div>
				))}
			</div>

			<div className="quotes-total">Totale: {functions.formatter.format(getSum())} </div>
		</>
	);
}

export default SplitPopup;
